import { Component, type ErrorInfo, type ReactNode } from 'react'
import { CrashScreen, NewVersionScreen } from '@/components/BootScreens'
import { isNewVersionError } from '@/lib/staleChunk'

interface Props {
  children: ReactNode
  /** `page` (inside the shell, around `<Outlet/>`): the rail survives a broken page.
   *  `app` (around everything, the last resort): a full-screen state with the brand lockup. */
  scope?: 'page' | 'app'
  /** a change (the path) clears the error: leaving a broken page through the rail works */
  resetKey?: string
}

interface State {
  error: Error | null
  key: string | undefined
}

/**
 * Catches render errors so one broken page doesn't white-screen the admin (WP-08). A failed
 * import of a page's code after a deploy is not a crash: the tab runs an old build, so it shows
 * «Вышла новая версия админки» with «Обновить страницу» (a retry would fail forever).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, key: this.props.resetKey }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.key) return { error: null, key: props.resetKey }
    return null
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('admin-ui error boundary:', error, info)
  }

  render(): ReactNode {
    const { error } = this.state
    if (error === null) return this.props.children
    const layout = this.props.scope === 'app' ? 'full' : 'inline'
    if (isNewVersionError(error)) return <NewVersionScreen layout={layout} />
    return <CrashScreen layout={layout} error={error} onRetry={() => this.setState({ error: null })} />
  }
}
